import { Fragment, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeftRight, ArrowUpRight, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { inr, sqm } from "@/lib/format";

export default function CompareTray({ items, onRemove, onClear, onExpress }) {
  const [open, setOpen] = useState(false);

  const acq = (p) => {
    const b = p.plotSqYd * p.pricePerSqYd;
    return b * 1.059 + b * 0.005 + 50000;
  };
  const y5 = (p) => p.plotSqYd * p.pricePerSqYd * Math.pow(1.12, 5);

  const bestRate = Math.min(...items.map((p) => p.pricePerSqYd), Infinity);
  const minExp = Math.min(...items.map((p) => p.expresswayKm), Infinity);
  const minAirport = Math.min(...items.map((p) => p.airportKm), Infinity);
  const maxY5 = Math.max(...items.map(y5), 0);

  const ROWS = [
    { label: "Zone / TP", v: (p) => `${p.zone} — ${p.zoneLabel}` },
    { label: "Sector", v: (p) => `Sector ${p.sector}` },
    { label: "Type", v: (p) => p.type },
    {
      label: "Plot Size",
      v: (p) => `${p.plotSqYd.toLocaleString("en-IN")} sq yd · ${sqm(p.plotSqYd)} sq m`,
    },
    {
      label: "Base Rate",
      v: (p) => `${inr(p.pricePerSqYd)}/sq yd`,
      best: (p) => p.pricePerSqYd === bestRate,
    },
    { label: "All-in Base", v: (p) => inr(p.plotSqYd * p.pricePerSqYd) },
    { label: "Acquisition Incl. Duties", v: (p) => inr(acq(p)) },
    { label: "RERA", v: (p) => (p.reraStatus === "REGISTERED" ? "Registered" : "Applied") },
    { label: "Expressway", v: (p) => `${p.expresswayKm} km`, best: (p) => p.expresswayKm === minExp },
    { label: "Airport", v: (p) => `${p.airportKm} km`, best: (p) => p.airportKm === minAirport },
    { label: "5-Yr Value @ 12%", v: (p) => inr(y5(p)), best: (p) => y5(p) === maxY5 },
  ];

  return (
    <>
      <AnimatePresence>
        {items.length > 0 && (
          <motion.div
            key="compare-tray"
            initial={{ y: 90, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 90, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 26 }}
            className="fixed bottom-5 left-5 z-40 flex max-w-[calc(100vw-2.5rem)] flex-wrap items-center gap-2 border border-border bg-card p-2.5 pl-4 shadow-2xl"
            data-testid="compare-tray"
          >
            <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              <ArrowLeftRight className="h-3.5 w-3.5 text-accent" />
              Compare
            </span>
            {items.map((p) => (
              <span
                key={p.id}
                className="flex items-center gap-1.5 border border-border bg-background px-2 py-1 font-mono text-[10px] uppercase tracking-[0.1em]"
              >
                {p.name}
                <button
                  onClick={() => onRemove(p.id)}
                  className="text-muted-foreground transition-colors duration-200 hover:text-destructive"
                  data-testid={`compare-remove-${p.id}`}
                  aria-label={`Remove ${p.name}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
            <Button
              onClick={() => setOpen(true)}
              disabled={items.length < 2}
              className="h-8 rounded-none px-3 font-mono text-[10px] uppercase tracking-[0.15em]"
              data-testid="compare-open-btn"
            >
              Compare {items.length}/3
            </Button>
            <button
              onClick={onClear}
              className="px-1 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground transition-colors duration-200 hover:text-foreground"
              data-testid="compare-clear-btn"
            >
              Clear
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-5xl rounded-none border-border"
          data-testid="compare-modal"
        >
          <DialogTitle className="font-display text-2xl font-medium uppercase tracking-tight">
            Side by side
          </DialogTitle>
          <DialogDescription className="text-xs">
            Duties at Gujarat schedule rates · 5-year value compounds at the 12%
            expected rate · accent marks the strongest cell in each row.
          </DialogDescription>

          <div className="mt-4 overflow-x-auto">
            <div className="min-w-[640px]">
              <div
                className="grid border border-border"
                style={{
                  gridTemplateColumns: `150px repeat(${items.length}, minmax(0, 1fr))`,
                }}
                data-testid="compare-table"
              >
                <div className="border-b border-r border-border bg-secondary/40 p-3" />
                {items.map((p) => (
                  <div key={p.id} className="border-b border-r border-border p-3 last:border-r-0">
                    <div className="h-16 w-full overflow-hidden border border-border">
                      <img
                        src={p.image}
                        alt={p.name}
                        className="h-full w-full object-cover saturate-[.55]"
                        loading="lazy"
                      />
                    </div>
                    <p className="mt-2 font-display text-sm font-medium tracking-tight">
                      {p.name}
                    </p>
                    <p className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-muted-foreground">
                      {p.zone} · SEC {p.sector}
                    </p>
                  </div>
                ))}

                {ROWS.map((row, ri) => (
                  <Fragment key={row.label}>
                    <div
                      className={`border-r border-border bg-secondary/40 p-3 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground ${
                        ri < ROWS.length - 1 ? "border-b" : ""
                      }`}
                    >
                      {row.label}
                    </div>
                    {items.map((p) => {
                      const isBest = row.best?.(p);
                      return (
                        <div
                          key={p.id}
                          className={`border-r border-border p-3 font-mono text-xs last:border-r-0 ${
                            ri < ROWS.length - 1 ? "border-b" : ""
                          } ${isBest ? "font-bold text-accent" : ""}`}
                          data-testid={
                            row.label === "Base Rate" ? `compare-rate-${p.id}` : undefined
                          }
                        >
                          {row.v(p)}
                          {isBest && <span className="ml-1.5 text-[9px] uppercase">Best</span>}
                        </div>
                      );
                    })}
                  </Fragment>
                ))}

                <div className="border-r border-border bg-secondary/40 p-3 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  Action
                </div>
                {items.map((p) => (
                  <div key={p.id} className="border-r border-border p-3 last:border-r-0">
                    <Button
                      onClick={() => {
                        setOpen(false);
                        onExpress(p);
                      }}
                      className="h-9 w-full rounded-none font-mono text-[10px] uppercase tracking-[0.15em]"
                      data-testid={`compare-interest-${p.id}`}
                    >
                      Interest
                      <ArrowUpRight className="ml-1 h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
